import { createFileRoute } from "@tanstack/react-router";
import { HubShell, Section, InfoGrid, FaqAccordion, hubHead } from "@/components/work-hubs/HubShell";

export const Route = createFileRoute("/remote-jobs/requirements")({
  head: () =>
    hubHead(
      "/remote-jobs/requirements",
      "Remote Job Requirements — Equipment, Internet & Payment Checklist | DrHint",
      "Hardware specs, internet speed minimums, PayPal/Wise payment setup and sample screening questions for non-phone remote jobs.",
    ),
  component: Page,
});

function Page() {
  return (
    <HubShell
      crumbs={[{ label: "Home", to: "/" }, { label: "Remote Jobs", to: "/remote-jobs" }, { label: "Requirements" }]}
      step={2}
      eyebrow="Remote Hub"
      title="Equipment & Verification Checklist"
      intro="Most rejections happen because of setup problems, not skills. Go through this checklist before applying so your application and identity checks pass the first time."
      back={{ to: "/remote-jobs", label: "Back to roles" }}
      next={{ to: "/remote-jobs/portals", label: "Proceed to Active Direct Apply Portals" }}
    >
      <Section title="Hardware">
        <InfoGrid items={[
          { title: "Computer", body: "Windows 10/11 or a recent macOS, 8 GB RAM recommended, a modern browser (Chrome or Edge). Some support roles block Chromebooks and phones." },
          { title: "Webcam", body: "Needed for identity verification and onboarding calls, even when the job itself has no calls." },
          { title: "Backup power", body: "A UPS or charged laptop battery helps you keep shifts during load-shedding." },
          { title: "Workspace", body: "A private, quiet place. Some clients require that nobody else can see your screen." },
        ]} />
      </Section>

      <Section title="Internet minimums">
        <p>Typical requirements are <b>10–25 Mbps download, 3–5 Mbps upload and under 100 ms ping</b>, often on a wired connection. Run a speed test at the time you plan to work and save the screenshot — some employers ask for it. A mobile hotspot is usually only accepted as a backup.</p>
      </Section>

      <Section title="Payment & identity verification">
        <InfoGrid items={[
          { title: "Payment account", body: "Many platforms pay via PayPal, Payoneer, Wise or direct bank transfer. Check which ones work in your country before signing up — PayPal personal accounts are not available everywhere, including Pakistan." },
          { title: "ID documents", body: "A valid national ID or passport that matches the name on your payment account exactly." },
          { title: "Tax details", body: "You may need to fill a W-8BEN (non-US) form. It is free and never needs a fee." },
          { title: "Red flag", body: "Anyone asking for your bank password, OTP or an upfront deposit is not an employer." },
        ]} />
      </Section>

      <Section title="Sample screening questions">
        <FaqAccordion faqs={[
          { q: "A customer is angry that their order is late. Write your first reply.", a: "Show empathy first, apologise briefly, state what you will check, and give a clear next step and timeframe. Keep it short and polite." },
          { q: "Does this post break the guideline 'no personal attacks'?", a: "Moderation tests give a rule and an example. Answer only from the rule given, not your own opinion, and be consistent." },
          { q: "Which of two AI answers is more helpful and accurate?", a: "Rating tests check whether you verify facts, notice unsafe content and follow the rubric exactly. Read the guidelines fully before starting." },
          { q: "Typing test", a: "Usually 3–5 minutes. Accuracy matters as much as speed — aim for 95%+ accuracy." },
        ]} />
      </Section>
    </HubShell>
  );
}
